import {
  createFileRoute,
  Link,
  useRouter,
} from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/lib/api";
import {
  gameQuery,
  gamePlayersQuery,
  meQuery,
  type GamePlayer,
} from "@/lib/queries";
import { useStartGame } from "@/lib/mutations";
import { useGameEvents } from "@/lib/sse";
import { Button, buttonStyles } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { CopyButton } from "@/components/CopyButton";
import { QRCode } from "@/components/QRCode";

export const Route = createFileRoute("/games/$id/lobby")({
  component: LobbyPage,
  // No auth guard: anonymous joiners (name only, no email) need
  // to land here right after the join form. Mutations are gated
  // server-side by the per-game player cookie.
  loader: async ({ context, params }) => {
    const [game, players] = await Promise.all([
      context.queryClient.ensureQueryData(gameQuery(params.id)),
      context.queryClient.ensureQueryData(gamePlayersQuery(params.id)),
    ]);
    return { game, players };
  },
});

function LobbyPage() {
  const router = useRouter();
  const { id } = Route.useParams();
  const qc = useQueryClient();

  // SSE replaces the 4s polling — the hub publishes "scoreboard"/"players"
  // events on join/start which invalidate these query keys.
  useGameEvents(id);

  const gameQ = useQuery(gameQuery(id));
  const playersQ = useQuery(gamePlayersQuery(id));
  const meQ = useQuery(meQuery);
  const start = useStartGame(id);

  if (!gameQ.data || !playersQ.data) return null;

  const game = gameQ.data;
  const players = playersQ.data;
  const me = meQ.data;

  const myPlayer = players.find(
    (p) => p.userId && me?.user && p.userId === me.user.id,
  );
  const isHost = !!myPlayer && myPlayer.id === game.hostPlayerId;

  // Game has already moved past lobby → suppress join card / start CTA.
  const hasStarted = game.status !== "lobby";

  // Join URL: prefer the cached join token (set by useCreateGame on host's
  // arrival). Falls back to the lobby URL if the host refreshed the page;
  // the QR PNG itself is server-rendered and always correct.
  const joinToken = qc.getQueryData<string>([
    "game",
    id,
    "joinToken",
  ] as const);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const joinUrl = joinToken
    ? `${origin}/j/${joinToken}`
    : `${origin}/games/${id}/lobby`;

  async function onStart() {
    if (start.isPending) return;
    try {
      const next = await start.mutateAsync();
      await router.navigate({
        to: "/games/$id/round/$n",
        params: { id, n: String(next.currentRound || 1) },
      });
    } catch {
      // surfaced below
    }
  }

  const startError =
    start.error instanceof ApiError ? start.error.message : null;

  return (
    <section className="mx-auto mt-6 max-w-2xl space-y-6 pb-32 md:pb-6">
      <header className="px-1">
        <h1 className="font-display text-3xl text-locomotive-900">Lobby</h1>
        <p className="font-mono text-xs text-muted-foreground">
          {game.totalRounds}-round game ·{" "}
          {game.rules.doubleBlankPenalty
            ? "double-blank = 50"
            : "double-blank = 0"}
          {hasStarted ? " · in progress" : ""}
        </p>
      </header>

      {!hasStarted && (
        <Card>
          <CardHeader>
            <CardTitle>Round 'em up</CardTitle>
            <p className="font-mono text-xs text-muted-foreground">
              Anyone with this link can join. No password required.
            </p>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center gap-4 md:flex-row md:items-start">
              <QRCode
                src={`/api/v1/games/${game.id}/qr.png`}
                alt="join QR code"
              />
              <div className="flex w-full flex-col gap-3">
                <code className="block break-all rounded-md border border-input bg-background/70 px-3 py-2 font-mono text-xs text-foreground">
                  {joinUrl}
                </code>
                <div className="flex justify-end">
                  <CopyButton value={joinUrl}>Copy link</CopyButton>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <section>
        <h2 className="font-display text-2xl mb-3">
          The boys{" "}
          <span className="font-mono text-xs text-muted-foreground">
            ({players.length})
          </span>
        </h2>
        {players.length === 0 ? (
          <Card>
            <CardContent>
              <p className="font-mono text-sm text-muted-foreground">
                Nobody's hopped on yet. Share the link.
              </p>
            </CardContent>
          </Card>
        ) : (
          <ul className="space-y-3">
            {players.map((p) => (
              <PlayerCard
                key={p.id}
                player={p}
                isMe={!!myPlayer && myPlayer.id === p.id}
              />
            ))}
          </ul>
        )}
      </section>

      {startError && (
        <div
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 font-mono text-sm text-destructive"
        >
          {startError}
        </div>
      )}

      {/* Host CTA: pinned bottom on mobile, right-aligned on desktop */}
      {isHost && !hasStarted && (
        <HostCta
          players={players}
          starting={start.isPending}
          onStart={onStart}
        />
      )}

      {!isHost && !hasStarted && (
        <p className="text-center font-mono text-sm text-muted-foreground">
          Waiting on the host to start the train.
        </p>
      )}

      {hasStarted && (
        <Card>
          <CardContent>
            <p className="font-mono text-sm text-foreground">
              Train's rolling —{" "}
              {game.status === "finished"
                ? "the run finished."
                : `stop ${game.currentRound} of ${game.totalRounds}.`}
            </p>
            <div className="mt-3 flex gap-2">
              {game.status !== "finished" && (
                <Link
                  to="/games/$id/round/$n"
                  params={{ id, n: String(game.currentRound) }}
                  className={buttonStyles("primary", "default")}
                >
                  Hop on
                </Link>
              )}
              <Link
                to="/games/$id/scoreboard"
                params={{ id }}
                className={buttonStyles("ghost", "default")}
              >
                Scoreboard
              </Link>
            </div>
          </CardContent>
        </Card>
      )}
    </section>
  );
}

interface PlayerCardProps {
  player: GamePlayer;
  isMe: boolean;
}

function PlayerCard({ player, isMe }: PlayerCardProps) {
  return (
    <li>
      <Card className="py-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="font-display text-lg text-foreground">
              {player.name}
              {isMe && (
                <span className="ml-2 font-mono text-xs text-muted-foreground">
                  (you)
                </span>
              )}
            </div>
            <div className="font-mono text-[11px] text-muted-foreground">
              joined {new Date(player.joinedAt).toLocaleTimeString()}
            </div>
          </div>
          {player.isHost && (
            <span
              className="rounded-full border border-ochre-500/50 bg-ochre-500/15 px-2 py-1 font-mono text-xs text-ochre-600"
              aria-label="host"
              title="host"
            >
              🎩 host
            </span>
          )}
        </div>
      </Card>
    </li>
  );
}

interface HostCtaProps {
  players: GamePlayer[];
  starting: boolean;
  onStart: () => void;
}

function HostCta({ players, starting, onStart }: HostCtaProps) {
  const alone = players.length <= 1;

  return (
    <>
      {/* Desktop */}
      <div className="hidden items-center justify-end gap-3 md:flex">
        {alone && (
          <button
            type="button"
            onClick={onStart}
            disabled={starting}
            className="font-mono text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline disabled:opacity-60"
          >
            go solo
          </button>
        )}
        <Button size="lg" onClick={onStart} disabled={starting || alone}>
          {starting
            ? "Whistling…"
            : alone
            ? "Waiting for the boys"
            : "Start the train"}
        </Button>
      </div>

      {/* Mobile pinned bottom */}
      <div className="fixed inset-x-0 bottom-0 z-10 flex flex-col items-stretch gap-2 border-t border-border/50 bg-sand-50/95 px-4 py-3 backdrop-blur md:hidden">
        <Button
          size="lg"
          onClick={onStart}
          disabled={starting || alone}
          className="w-full"
        >
          {starting
            ? "Whistling…"
            : alone
            ? "Waiting for the boys"
            : "Start the train"}
        </Button>
        {alone && (
          <button
            type="button"
            onClick={onStart}
            disabled={starting}
            className="self-center font-mono text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline disabled:opacity-60"
          >
            go solo
          </button>
        )}
      </div>
    </>
  );
}
